/**
 * Panel del manager (T3): lo que su equipo tiene sugerido y todavía nadie confirmó, con los
 * botones Confirmar / Cerrar / Corregir, y "Declarar asignación" para lo que la app no tiene.
 * Solo botones: el agente (T3b) propone, nunca cierra estados (lección del Mapa de Talento).
 * El backend re-resuelve al escribir que quien aprieta es el líder directo o el roster.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { InternalTable, EmptyState } from '@/components/internal/shared';
import { toastError } from '@/lib/toast-error';
import { useOrgClientsAll, useOrgManagerMutations } from '@/hooks/useOrg';
import { ClientPicker } from '@/components/org/ClientPicker';
import { OrgClientLink, OrgOriginBadge, OrgPersonLink, OrgSolutionLink, type OrgAssignment, type OrgPerson } from '@/components/org/org-ui';
import { ORG_SOLUTIONS } from '../../../../shared/types/org';
import { isManagerEditable } from '../../../../shared/org-rules';
import { normalizeEmail } from '../../../../shared/identity';
import { useAuth } from '@/contexts/SharedAuthContext';

/** Persona del equipo tal como la devuelve `org.team` (con si reporta directo al líder). */
export type TeamMember = OrgPerson & { isDirect?: boolean };

export function OrgManagerPanel({ people, assignments, canViewAll }: { people: readonly TeamMember[]; assignments: readonly OrgAssignment[]; canViewAll: boolean }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const viewer = normalizeEmail(user?.email);
  const m = useOrgManagerMutations();
  const [closing, setClosing] = useState<OrgAssignment | null>(null);
  const [closeOpen, setCloseOpen] = useState(false);
  const [correcting, setCorrecting] = useState<OrgAssignment | null>(null);
  const [declaring, setDeclaring] = useState(false);
  const pending = assignments.filter((a) => a.status === 'suggested');
  const onError = (e: unknown) => toastError(e, 'org.team.toast_error');
  // Misma regla que el backend (canWriteAssignment): roster, líder DIRECTO o manager del sync. Un líder de
  // líderes ve la fila pero no los botones (antes terminaba en un FORBIDDEN sorpresa).
  const direct = new Set(people.filter((p) => p.isDirect).map((p) => p.employee_id));
  const canWrite = (a: OrgAssignment) => canViewAll || direct.has(a.employee_id) || (!!viewer && normalizeEmail(a.manager_email) === viewer);
  const writablePeople = canViewAll ? people : people.filter((p) => p.isDirect);

  const confirm = (a: OrgAssignment) =>
    m.confirm.mutate({ id: a.id }, { onSuccess: () => toast.success(t('org.team.toast_confirmed', 'Confirmada')), onError });

  const columns = [
    { key: 'person', label: t('org.table.person', 'Persona'), render: (a: OrgAssignment) => <OrgPersonLink email={a.email} name={a.full_name} /> },
    { key: 'client', label: t('org.table.client', 'Cliente'), render: (a: OrgAssignment) => <OrgClientLink clientId={a.client_id} name={a.display_name} /> },
    { key: 'solution', label: t('org.table.solution', 'Solución'), render: (a: OrgAssignment) => <OrgSolutionLink slug={a.solution_slug} /> },
    { key: 'pct', label: t('org.table.pct', 'Dedicación'), align: 'right' as const, render: (a: OrgAssignment) => (a.pct == null ? '—' : `${Math.round(a.pct)} %`) },
    { key: 'origin', label: t('org.table.origin', 'Origen'), render: (a: OrgAssignment) => <OrgOriginBadge origin={a.origin} /> },
    { key: 'period', label: t('org.table.period', 'Mes'), render: (a: OrgAssignment) => <span className="text-muted-foreground">{a.period}</span> },
    {
      key: 'actions', label: '', align: 'right' as const,
      render: (a: OrgAssignment) =>
        canWrite(a) ? (
          <div className="flex justify-end gap-1">
            <Button size="sm" disabled={m.busy} onClick={() => confirm(a)}>{t('org.team.confirm', 'Confirmar')}</Button>
            {isManagerEditable(a) && <Button size="sm" variant="outline" disabled={m.busy} onClick={() => setCorrecting(a)}>{t('org.team.correct', 'Corregir')}</Button>}
            <Button size="sm" variant="ghost" disabled={m.busy} onClick={() => { setClosing(a); setCloseOpen(true); }}>{t('org.team.close', 'Cerrar')}</Button>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">{t('org.team.not_direct', 'La confirma su líder directo')}</span>
        ),
    },
  ];

  return (
    <section className="space-y-3 rounded-xl border p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">{t('org.team.pending_title', 'Pendientes de confirmar')}</h3>
          <p className="text-xs text-muted-foreground">{t('org.team.pending_help', 'Lo que la app de Carga de Horas trae sin lock del mes, o lo que alguien sugirió. Confirmar deja constancia de quién y cuándo; cerrar dice que esa persona ya no trabaja con ese cliente y el sync no lo reabre. Las de la app se corrigen en la app.')}</p>
        </div>
        {writablePeople.length > 0 && <Button size="sm" variant="outline" disabled={m.busy} onClick={() => setDeclaring(true)}><Plus className="mr-1 h-4 w-4" />{t('org.team.declare', 'Declarar asignación')}</Button>}
      </div>
      {pending.length ? <InternalTable columns={columns} rows={pending} rowKey={(a) => a.id} /> : <EmptyState title={t('org.team.no_pending', 'Nada pendiente: todo lo de tu equipo está confirmado.')} />}

      {/* El Action no cierra solo (preventDefault): se cierra en onSuccess; `closing` se conserva para que el texto no parpadee en la animación. */}
      <AlertDialog open={closeOpen} onOpenChange={(o) => { if (!o && !m.busy) setCloseOpen(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('org.team.close_title', '¿Cerrar esta asignación?')}</AlertDialogTitle>
            <AlertDialogDescription>{t('org.team.close_body', '{{person}} deja de figurar como asignada a {{client}}. Queda en el historial y el sync diario no la vuelve a abrir mientras la app la siga trayendo; si más adelante corresponde, se puede reabrir.', { person: closing?.full_name ?? '', client: closing?.display_name ?? '' })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={m.busy}>{t('org.team.cancel', 'Cancelar')}</AlertDialogCancel>
            <AlertDialogAction disabled={m.busy} onClick={(e) => { e.preventDefault(); if (closing) m.close.mutate({ id: closing.id }, { onSuccess: () => { toast.success(t('org.team.toast_closed', 'Cerrada')); setCloseOpen(false); }, onError }); }}>{t('org.team.close', 'Cerrar')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {correcting && <CorrectDialog assignment={correcting} onClose={() => setCorrecting(null)} />}
      {declaring && <DeclareDialog people={writablePeople} onClose={() => setDeclaring(false)} />}
    </section>
  );
}

function CorrectDialog({ assignment, onClose }: { assignment: OrgAssignment; onClose: () => void }) {
  const { t } = useTranslation();
  const m = useOrgManagerMutations();
  const [pct, setPct] = useState(assignment.pct == null ? '' : String(assignment.pct));
  const [role, setRole] = useState(assignment.role_title ?? '');
  const pctValid = pct === '' || (Number.isFinite(Number(pct)) && Number(pct) >= 0 && Number(pct) <= 100);
  const save = () =>
    m.correct.mutate(
      { id: assignment.id, pct: pct === '' ? null : Number(pct), roleTitle: role.trim() || null },
      { onSuccess: () => { toast.success(t('org.team.toast_corrected', 'Corregida')); onClose(); }, onError: (e) => toastError(e, 'org.team.toast_error') },
    );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('org.team.correct_title', 'Corregir asignación')}</DialogTitle>
          <DialogDescription>{assignment.full_name} · {assignment.display_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="org-pct">{t('org.table.pct', 'Dedicación')} (%)</Label>
            <Input id="org-pct" type="number" min={0} max={100} value={pct} onChange={(e) => setPct(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="org-role">{t('org.table.role', 'Puesto')}</Label>
            <Input id="org-role" value={role} onChange={(e) => setRole(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={m.busy} onClick={onClose}>{t('org.team.cancel', 'Cancelar')}</Button>
          <Button disabled={m.busy || !pctValid} onClick={save}>{t('org.team.save', 'Guardar')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeclareDialog({ people, onClose }: { people: readonly TeamMember[]; onClose: () => void }) {
  const { t } = useTranslation();
  const m = useOrgManagerMutations();
  const clients = useOrgClientsAll();
  const [employeeId, setEmployeeId] = useState<string>('');
  const [clientId, setClientId] = useState<string | null>(null);
  const [solution, setSolution] = useState<string>('');
  const [pct, setPct] = useState('');
  const solutions = ORG_SOLUTIONS.filter((s) => s.worksWithClients);
  const person = people.find((p) => String(p.employee_id) === employeeId);
  const pctValid = pct === '' || (Number.isFinite(Number(pct)) && Number(pct) >= 0 && Number(pct) <= 100);
  const canSave = !!person && !!clientId && !!solution && pctValid && !m.busy;
  const save = () =>
    person && clientId && solution &&
    m.declare.mutate(
      { employeeId: person.employee_id, clientId, solutionSlug: solution, pct: pct === '' ? null : Number(pct) },
      { onSuccess: () => { toast.success(t('org.team.toast_declared', 'Declarada y confirmada')); onClose(); }, onError: (e) => toastError(e, 'org.team.toast_error') },
    );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('org.team.declare_title', 'Declarar asignación')}</DialogTitle>
          <DialogDescription>{t('org.team.declare_help', 'Para lo que la app de Carga de Horas no tiene (Partnerships, Business, team Data o alguien que no carga). Nace confirmada por vos, en el mes en curso.')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="org-declare-person">{t('org.table.person', 'Persona')}</Label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger id="org-declare-person"><SelectValue placeholder={t('org.team.person_placeholder', 'Elegir persona de tu equipo…')} /></SelectTrigger>
              <SelectContent>
                {people.map((p) => <SelectItem key={p.employee_id} value={String(p.employee_id)}>{p.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="org-declare-client">{t('org.table.client', 'Cliente')}</Label>
            <ClientPicker id="org-declare-client" clients={clients.data ?? []} value={clientId} onChange={setClientId} loading={clients.isLoading} error={clients.isError} onRetry={() => clients.refetch()} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="org-declare-solution">{t('org.table.solution', 'Solución')}</Label>
            <Select value={solution} onValueChange={setSolution}>
              <SelectTrigger id="org-declare-solution"><SelectValue placeholder={t('org.team.solution_placeholder', 'Elegir solución…')} /></SelectTrigger>
              <SelectContent>
                {solutions.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="org-declare-pct">{t('org.table.pct', 'Dedicación')} (%)</Label>
            <Input id="org-declare-pct" type="number" min={0} max={100} value={pct} onChange={(e) => setPct(e.target.value)} placeholder={t('org.team.pct_optional', 'Opcional')} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={m.busy} onClick={onClose}>{t('org.team.cancel', 'Cancelar')}</Button>
          <Button disabled={!canSave} onClick={save}>{t('org.team.declare_save', 'Declarar')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
